import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { api } from "../utils/api";
import {
  PORTAL_EMAIL_KEY,
  PORTAL_TOKEN_KEY,
  PORTAL_USER_ID_KEY,
  clearPortalSession,
  verifyPortalSession,
} from "../utils/auth";
import PasswordField from "../../shared/PasswordField";
import { PortalPageFallback } from "../components/PortalLoader";
import {
  loadRememberedLogin,
  onRememberMeToggle,
  persistRememberedLogin,
} from "../../shared/loginRememberMe";

const REMEMBER_PREFIX = "enterprise_portal";

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [bootChecking, setBootChecking] = useState(true);
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    const { rememberMe: savedRemember, identifier } = loadRememberedLogin(REMEMBER_PREFIX);
    setRememberMe(savedRemember);
    if (savedRemember && identifier) {
      setEmail(identifier);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ok = await verifyPortalSession();
        if (cancelled) return;
        if (ok) {
          navigate("/enterprise-portal/home", { replace: true });
        } else {
          clearPortalSession();
        }
      } catch {
        /* stay on login */
      } finally {
        if (!cancelled) setBootChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api("/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Login failed");
        return;
      }
      localStorage.removeItem(PORTAL_TOKEN_KEY);
      localStorage.setItem(PORTAL_USER_ID_KEY, String(data.userId));
      localStorage.setItem(PORTAL_EMAIL_KEY, data.email || email);
      persistRememberedLogin(REMEMBER_PREFIX, rememberMe, email.trim());
      window.location.assign("/enterprise-portal/home");
    } catch {
      setError("Unable to reach Enterprise Portal API");
    } finally {
      setLoading(false);
    }
  };

  if (bootChecking) {
    return <PortalPageFallback message="Checking session" submessage="Enterprise portal…" />;
  }

  return (
    <div
      className="enterprise-portal-shell portal-nye-theme min-h-screen flex items-center justify-center px-4"
      style={{ background: "var(--app-background, #f2f6f7)" }}
    >
      <form onSubmit={onSubmit} className="w-full max-w-md theme-panel rounded-2xl p-8 space-y-5 shadow-lg">
        <div>
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#80489a]">Nyafaz</p>
          <h1 className="text-2xl font-bold theme-heading mt-1">Enterprise portal</h1>
          <p className="text-sm theme-body mt-1 opacity-80">Create and manage Super Admin organizations</p>
        </div>
        {error ? <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div> : null}
        <label className="block space-y-1">
          <span className="text-sm font-medium text-[#3b3350]">Email</span>
          <input
            type="email"
            required
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full h-11 rounded-lg border border-[#d9d0e3] px-3 outline-none focus:border-[#80489a] bg-white text-[#1f1630]"
          />
        </label>
        <label className="block space-y-1">
          <span className="text-sm font-medium text-[#3b3350]">Password</span>
          <PasswordField
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            inputClassName="w-full h-11 rounded-lg border border-[#d9d0e3] px-3 pr-10 outline-none focus:border-[#80489a] bg-white text-[#1f1630]"
            toggleClassName="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-[#6b6280] hover:text-[#3b3350]"
          />
        </label>
        <label className="flex items-center gap-2 text-sm text-[#6b6280] cursor-pointer">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => onRememberMeToggle(REMEMBER_PREFIX, e.target.checked, setRememberMe)}
            className="rounded border-[#d9d0e3]"
          />
          Remember me
        </label>
        <button
          type="submit"
          disabled={loading}
          className="w-full h-11 rounded-lg bg-[#80489a] text-white font-semibold hover:bg-[#6b3a82] disabled:opacity-60"
        >
          {loading ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
};

export const RequirePortalAuth = ({ children }) => {
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ok = await verifyPortalSession();
        if (cancelled) return;
        if (ok) {
          setStatus("ok");
          return;
        }
        clearPortalSession();
        setStatus("denied");
      } catch {
        if (!cancelled) {
          clearPortalSession();
          setStatus("denied");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (status === "denied") {
    return <Navigate to="/enterprise-portal" replace />;
  }
  if (status === "checking") {
    return <PortalPageFallback message="Checking session" submessage="Enterprise portal…" />;
  }
  return children;
};

export default Login;
